import Link from "next/link";
import { Container, Row, Col } from "react-bootstrap";
import { FaArrowLeft } from "react-icons/fa";
import { LayoutOne } from "@/layouts";
import BlogItemTwo from "@/components/blog/blogItemTwo";
import BlogSideBar from "@/components/blog/sidebar";
import SeoHead from "@/components/SeoHead";
import PageViewBeacon from "@/components/PageViewBeacon";
import OurGroupSection from "@/components/ourGroup";
import {
  getCategories,
  getPosts,
  getTags,
  getAuthors,
} from "@/lib/cms";
import { getLatestBlogs } from "@/lib/blog";
import { buildTaxonomySchema } from "@/lib/taxonomySchema";

export async function getStaticPaths() {
  const categories = await getCategories();
  return {
    paths: categories.map((c) => ({ params: { slug: c.slug } })),
    fallback: "blocking",
  };
}

export async function getStaticProps({ params }) {
  const slug = String(params.slug || "");
  const [categories, tags, latest, authors] = await Promise.all([
    getCategories(),
    getTags(),
    getLatestBlogs(4),
    getAuthors(),
  ]);
  const matched = categories.find((c) => c.slug === slug) || null;
  const { data: posts } = await getPosts({ category: slug, perPage: 100 });
  return {
    props: {
      category: matched,
      slug,
      posts,
      latest,
      categories,
      tags,
      authors,
    },
    revalidate: 3600,
  };
}

function CategoryPage({ category, slug, posts = [], latest = [], categories = [], tags = [], authors = [] }) {
  const pageTitle = category
    ? category.name.charAt(0).toUpperCase() + category.name.slice(1)
    : slug;
  const seo = {
    seo_title: category ? `${pageTitle} — Blog Posts` : pageTitle,
    meta_description: category && category.description
      ? category.description
      : `Latest blog posts in ${pageTitle}.`,
    focus_keyword: pageTitle,
    schema_json: buildTaxonomySchema({
      kind: "category",
      slug,
      displayName: pageTitle,
      description: category && category.description
        ? category.description
        : `Latest blog posts in ${pageTitle}.`,
    }),
  };

  return (
    <>
      <PageViewBeacon contentType="category" slug={slug} path={`/category/${slug}`} />
      <LayoutOne topbar={true} banner={true} showHeading={false}>
      <SeoHead
        seo={seo}
        type="website"
        path={`/category/${slug}`}
        fallbackTitle={pageTitle}
        fallbackDescription={seo.meta_description}
        extraKeywords={[pageTitle]}
        publisherName="Siddhivinayak Group"
      />
      <div className="ltn__blog-area mb-120">
        <Container>
          <div className="section-title-area text-center mb-40">
            <h1 className="page-heading-title">
              {pageTitle}
              <span className="text-muted fs-6 ms-2">
                ({posts.length} {posts.length === 1 ? "post" : "posts"})
              </span>
            </h1>
            <p className="mb-0">
              <Link href="/blog" className="ltn__back-link">
                <FaArrowLeft /> Back to all posts
              </Link>
            </p>
          </div>
          <Row>
            <Col xs={12} lg={8}>
              <Row className="blog-card-grid">
                {posts.length ? (
                  posts.map((blog, key) => (
                    <Col key={key} xs={12} md={6}>
                      <BlogItemTwo
                        blogData={blog}
                        slug={blog.slug}
                        baseUrl="blog"
                      />
                    </Col>
                  ))
                ) : (
                  <Col xs={12}>
                    <div className="text-center py-5">
                      <h3 className="mb-3">No posts in this category</h3>
                      <p className="mb-4">
                        We couldn't find any blog posts for{" "}
                        <strong>{pageTitle}</strong>.
                      </p>
                      <Link href="/blog" className="btn theme-btn-1">
                        <FaArrowLeft /> Back to all posts
                      </Link>
                    </div>
                  </Col>
                )}
              </Row>
            </Col>

            <Col xs={12} lg={{ span: 4, order: 0 }}>
              <BlogSideBar
                latestdBlogs={latest}
                categories={categories}
                tags={tags}
                authors={authors}
                showAgent={false}
                showBookForm={false}
                showTopRated={false}
                showAuthors={false}
                showTwitter={false}
                showFollowUs={false}
                activeCategory={category ? category.name : null}
              />
            </Col>
          </Row>
        </Container>
      </div>
      <div className="section-bg-1">
        <OurGroupSection />
      </div>
    </LayoutOne>
    </>
  );
}

export default CategoryPage;
